import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { imageUrl } from '../lib/images'

const WORKSHOP_SERVICES = [
  { label: 'Serviceeftersyn', href: '/serviceeftersyn' },
  { label: 'Dækhotel', href: '/daekhotel' },
  { label: 'Dækskift', href: '/daekskift' },
  { label: 'Elbil service', href: '/elbil-service' },
  { label: 'Montering af vinterdæk', href: '/vinterdaek' },
  { label: 'Autoværksted Billund', href: '/autovaerksted-billund' },
  { label: 'Autoværksted Grindsted', href: '/autovaerksted-grindsted' },
]

export default function Header({ settings }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [workshopDropdownOpen, setWorkshopDropdownOpen] = useState(false)
  const [mobileWorkshopOpen, setMobileWorkshopOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()
  const dropdownRef = useRef(null)
  const hoverTimeoutRef = useRef(null)

  const bookingUrl = settings?.booking_url || 'https://service.dinbilpartner.dk/book-tid'
  const phone = settings?.phone || '22 18 73 30'
  const phoneRaw = settings?.phone_raw || '22187330'
  const email = settings?.email || 'steen@ekbiler.dk'
  const address = settings?.address || 'Mejerivej 12, 7250 Hejnsvig'

  // Stable scroll listener with hysteresis to eliminate jumping/shaking
  useEffect(() => {
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY
          setIsScrolled((prev) => {
            // Activate sticky scrolled mode when scrolled past 50px
            // Deactivate only when scrolling back up above 20px
            // The 30px deadband completely eliminates threshold oscillation/shaking
            if (!prev && scrollY > 50) return true
            if (prev && scrollY < 20) return false
            return prev
          })
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false)
    setWorkshopDropdownOpen(false)
    setMobileWorkshopOpen(false)
  }, [location.pathname])

  // Clean outside click handler that doesn't intercept touch links
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setWorkshopDropdownOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  // Hover handlers with grace period for desktop dropdown
  const handleDropdownMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    setWorkshopDropdownOpen(true)
  }

  const handleDropdownMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    hoverTimeoutRef.current = setTimeout(() => {
      setWorkshopDropdownOpen(false)
    }, 220)
  }

  const isWorkshopActive = WORKSHOP_SERVICES.some((s) => location.pathname === s.href)
  const isHomePage = location.pathname === '/'

  return (
    <>
      {/* Main Sticky Header with smooth transitions */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-[#14171a]/95 backdrop-blur-md border-b border-white/10 shadow-lg'
            : isHomePage
            ? 'bg-transparent border-b border-white/10 shadow-none'
            : 'bg-[#14171a] border-b border-[#262c33] shadow-xs'
        }`}
      >
        <div
          className={`max-w-7xl mx-auto px-3 sm:px-6 flex items-center justify-between gap-2 sm:gap-4 transition-all duration-300 ease-in-out ${
            isScrolled ? 'h-16' : 'h-20'
          }`}
        >
          {/* Logo with smooth size transition */}
          <Link
            to="/"
            className="flex items-center gap-2 sm:gap-3 shrink-0 focus:outline-none focus:ring-2 focus:ring-red-600 rounded-lg p-0.5 sm:p-1 group"
            aria-label="EK Biler Forside"
          >
            <img
              src={imageUrl('ekbiler-logo.png', 'width=360,quality=90,format=auto')}
              alt="EK Biler v/Steen Svensson Ek"
              className={`w-auto max-w-[105px] sm:max-w-[250px] object-contain transition-all duration-300 ease-in-out will-change-transform ${
                isScrolled ? 'h-6 sm:h-7.5 max-h-6 sm:max-h-7.5' : 'h-6.5 sm:h-10 max-h-6.5 sm:max-h-10'
              }`}
            />
          </Link>

          {/* Desktop Navigation: Visible on screens >= 1200px ONLY as requested */}
          <nav
            className="hidden min-[1200px]:flex items-center space-x-1"
            aria-label="Hovednavigation"
          >
            <Link
              to="/brugte-biler"
              className={`px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                location.pathname === '/brugte-biler'
                  ? 'text-white bg-[#e20019]'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              Brugte biler
            </Link>

            {/* Dropdown for Autoværksted with Hover Bridge & Grace Period */}
            <div
              className="relative"
              ref={dropdownRef}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleDropdownMouseLeave}
            >
              <button
                type="button"
                onClick={() => setWorkshopDropdownOpen(!workshopDropdownOpen)}
                aria-expanded={workshopDropdownOpen}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                  isWorkshopActive
                    ? 'text-white bg-[#e20019]'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                <span>Autoværksted</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    workshopDropdownOpen ? 'rotate-180 text-[#e20019]' : 'text-white/70'
                  }`}
                  aria-hidden="true"
                />
              </button>

              {/* Dropdown Box with invisible hover bridge (pt-2) */}
              {workshopDropdownOpen && (
                <div className="absolute left-0 top-full pt-1.5 w-64 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                  <div className="rounded-xl bg-white shadow-xl ring-1 ring-black/10 border border-gray-100 py-2">
                    <div className="px-3.5 py-1.5 text-[11px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1 flex items-center justify-between">
                      <span>Vores Værkstedsydelser</span>
                      <i className="fa-solid fa-wrench text-gray-400 text-[10px]" aria-hidden="true" />
                    </div>
                    {WORKSHOP_SERVICES.map((item) => {
                      const isActive = location.pathname === item.href
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setWorkshopDropdownOpen(false)}
                          className={`flex items-center justify-between px-3.5 py-2.5 text-sm transition-all ${
                            isActive
                              ? 'text-[#e20019] font-bold bg-red-50 border-l-4 border-[#e20019] shadow-xs'
                              : 'text-gray-700 hover:text-[#e20019] hover:bg-gray-50 font-medium'
                          }`}
                        >
                          <span>{item.label}</span>
                          <i
                            className={`fa-solid fa-angle-right text-xs transition-transform ${
                              isActive ? 'text-[#e20019] translate-x-0.5' : 'text-gray-300'
                            }`}
                            aria-hidden="true"
                          />
                        </Link>
                      )
                    })}
                    <div className="mt-2 pt-2 border-t border-gray-100 px-3.5">
                      <div className="text-[11px] text-gray-500 flex items-center gap-1.5 font-medium">
                        <i className="fa-solid fa-check text-emerald-600" aria-hidden="true" />
                        <span>Gratis lånebil til rådighed</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/finansering"
              className={`px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                location.pathname === '/finansering'
                  ? 'text-white bg-[#e20019]'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              Finansiering/Forsikring
            </Link>

            <a
              href={settings?.indicata_url || 'https://lg.indicata.com/#/?dealerId=156f3c15-a501-4045-bf19-5598ec416fd9'}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 text-sm font-semibold text-white/90 hover:text-white hover:bg-white/10 rounded-md transition-colors flex items-center gap-1"
            >
              <span>Beregn byttepris</span>
              <i className="fa-solid fa-arrow-up-right-from-square text-[10px] text-white/70" aria-hidden="true" />
            </a>

            <Link
              to="/om-os"
              className={`px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                location.pathname === '/om-os'
                  ? 'text-white bg-[#e20019]'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              Om os
            </Link>

            <Link
              to="/kontakt"
              className={`px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                location.pathname === '/kontakt'
                  ? 'text-white bg-[#e20019]'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              Kontakt
            </Link>
          </nav>

          {/* Action CTAs (Desktop & Mobile) */}
          <div className="flex items-center gap-1 sm:gap-2.5 shrink-0">
            {/* Primary High-Priority "Book tid" CTA */}
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 bg-[#e20019] hover:bg-[#b80014] text-white px-3 sm:px-5 py-2 sm:py-2.5 rounded-none font-bold text-xs sm:text-sm uppercase tracking-wider shadow-sm transition-all whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600"
            >
              <i className="fa-solid fa-calendar-check text-xs sm:text-sm" aria-hidden="true" />
              <span>Book tid</span>
            </a>

            {/* Burger Menu Button: Visible on screens < 1200px ONLY as requested */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`min-[1200px]:hidden inline-flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 ${
                mobileMenuOpen
                  ? 'bg-white border-white text-black hover:bg-gray-100'
                  : 'border-white/30 text-white hover:bg-white/10'
              }`}
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
            >
              <i
                className={`fa-solid ${mobileMenuOpen ? 'fa-xmark text-black' : 'fa-bars'} text-base sm:text-xl`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu Drawer (< 1200px) - Independently Scrollable & Touch-Friendly */}
        {mobileMenuOpen && (
          <div className="min-[1200px]:hidden bg-white border-t border-gray-200 px-4 pt-3 pb-28 shadow-2xl animate-in slide-in-from-top duration-200 max-h-[calc(100dvh-4.5rem)] overflow-y-auto overflow-x-hidden overscroll-contain">
            {/* Quick Contact Header inside Mobile Drawer */}
            <div className="bg-gray-50 rounded-xl p-3 mb-4 flex items-center justify-between border border-gray-200">
              <a
                href={`tel:${phoneRaw}`}
                className="flex items-center gap-2 text-sm font-bold text-gray-900 whitespace-nowrap"
              >
                <div className="w-8 h-8 rounded-full bg-red-100 text-[#e20019] flex items-center justify-center">
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                </div>
                <span>{phone}</span>
              </a>
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#e20019] hover:bg-[#b80014] text-white px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors whitespace-nowrap"
              >
                Book tid online
              </a>
            </div>

            <div className="space-y-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-lg text-base font-semibold ${
                  location.pathname === '/' ? 'text-[#e20019] bg-red-50 font-bold' : 'text-gray-800 hover:bg-gray-50'
                }`}
              >
                Forside
              </Link>

              <Link
                to="/brugte-biler"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-lg text-base font-semibold ${
                  location.pathname === '/brugte-biler'
                    ? 'text-[#e20019] bg-red-50 font-bold'
                    : 'text-gray-800 hover:bg-gray-50'
                }`}
              >
                Brugte biler
              </Link>

              {/* Mobile Workshop Accordion - Fully click-through for sublinks */}
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setMobileWorkshopOpen(!mobileWorkshopOpen)}
                  className="w-full flex items-center justify-between px-3 py-2.5 text-base font-semibold text-gray-800 bg-gray-50/70 hover:bg-gray-100 transition-colors"
                >
                  <span className={isWorkshopActive ? 'text-[#e20019] font-bold' : ''}>Autoværksted</span>
                  <i
                    className={`fa-solid fa-chevron-down text-xs transition-transform ${
                      mobileWorkshopOpen ? 'rotate-180 text-[#e20019]' : 'text-gray-400'
                    }`}
                    aria-hidden="true"
                  />
                </button>

                {mobileWorkshopOpen && (
                  <div className="bg-white py-1.5 pl-3 pr-2 space-y-1 border-t border-gray-100">
                    {WORKSHOP_SERVICES.map((item) => {
                      const isActive = location.pathname === item.href
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => {
                            setMobileMenuOpen(false)
                            setMobileWorkshopOpen(false)
                          }}
                          className={`flex items-center justify-between py-2.5 px-3 text-sm rounded-lg transition-all ${
                            isActive
                              ? 'text-[#e20019] font-bold bg-red-50 border-l-4 border-[#e20019]'
                              : 'text-gray-700 hover:text-[#e20019] hover:bg-gray-50 font-medium'
                          }`}
                        >
                          <span>{item.label}</span>
                          <i
                            className={`fa-solid fa-angle-right text-xs ${
                              isActive ? 'text-[#e20019]' : 'text-gray-300'
                            }`}
                            aria-hidden="true"
                          />
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>

              <Link
                to="/finansering"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-lg text-base font-semibold ${
                  location.pathname === '/finansering'
                    ? 'text-[#e20019] bg-red-50 font-bold'
                    : 'text-gray-800 hover:bg-gray-50'
                }`}
              >
                Finansiering/Motor forsikring
              </Link>

              <a
                href={settings?.indicata_url || 'https://lg.indicata.com/#/?dealerId=156f3c15-a501-4045-bf19-5598ec416fd9'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg text-base font-semibold text-gray-800 hover:bg-gray-50"
              >
                <span>Beregn byttepris</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-xs text-gray-400" aria-hidden="true" />
              </a>

              <Link
                to="/om-os"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-lg text-base font-semibold ${
                  location.pathname === '/om-os' ? 'text-[#e20019] bg-red-50 font-bold' : 'text-gray-800 hover:bg-gray-50'
                }`}
              >
                Om os
              </Link>

              <Link
                to="/kontakt"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-lg text-base font-semibold ${
                  location.pathname === '/kontakt' ? 'text-[#e20019] bg-red-50 font-bold' : 'text-gray-800 hover:bg-gray-50'
                }`}
              >
                Kontakt
              </Link>
            </div>

            {/* Mobile Footer Links in menu */}
            <div className="mt-5 pt-4 border-t border-gray-200 text-xs text-gray-500 space-y-2">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-location-dot text-[#e20019]" aria-hidden="true" />
                <span>{address}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-envelope text-gray-400" aria-hidden="true" />
                <span className="whitespace-nowrap">{email}</span>
              </div>
              <div className="pt-2 flex items-center gap-3">
                <a
                  href={settings?.facebook_url || 'https://www.facebook.com/ekbiler.dk/'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-blue-600 font-semibold text-xs"
                >
                  <i className="fa-brands fa-facebook text-base" aria-hidden="true" />
                  <span>Følg os på Facebook</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  )
}
